import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { Box, Icon, ScrollView, Spinner } from '@rbilabs/universal-components';
import { router } from 'expo-router';
import { useStoreContext } from 'features/store';
import { isObject } from 'lodash-es';
import { useIntl } from 'react-intl';
import { TouchableOpacity, View } from 'react-native';

import { IRestaurant } from '@rbi-ctg/store';
import { addressReducer } from 'components/address-modal/reducer';
import { CardModal } from 'components/card-modal';
import Dialog from 'components/dialog';
import {
  DeliveryStatus,
  DeliveryStoreStatus,
  IDeliveryAddress,
  useDeliveryRestaurantLazyQuery,
} from 'generated/graphql-gateway';
import { ISaveDeliveryAddressInput, IsoCountryCode } from 'generated/graphql-gateway';
import usePlaceIdDetails from 'hooks/geolocation/use-place-id-details';
import useErrorModal from 'hooks/use-error-modal';
import { useDismissAndReplace } from 'navigation/hooks/use-dismiss-and-navigate';
import {
  DeliveryStoreStatusToStoreStatusMap,
  IGetClosestAvailableDeliveryRestaurantResult,
} from 'remote/api/restaurants';
import { CustomEventNames, EventTypes, logNavigationClick, logRBIEvent } from 'state/amplitude';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode, useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { primitive } from 'styles/constants/primitives';
import { convertMilesToMeters } from 'utils/distance';
import { isNativeIOS } from 'utils/environment';
import { platform } from 'utils/environment';
import { IPlaceAddress, IPlaceData } from 'utils/geolocation';
import { routes } from 'utils/routing';

import { DeliveryAddressSelectHandle, DeliveryAddresses } from './delivery-addresses';
import { ButtonContainer } from './delivery-addresses/styled';
import { DeliveryTermsContent } from './delivery-terms/delivery-terms-content';
import { DeliveryUnavailable } from './delivery-unavailable';
import { DeliveryAddressForm, DeliveryAddressFormHandle } from './new-address-form';
import { DeliveryAddressFormFooter } from './new-address-form/delivery-address-form-footer';
import { DeliverHereButton, DeliveryModalContainer } from './styled';
import { ActionStatus, FormStatus, SelectedDeliveryAddress } from './types';
import { addressExists, getAddressLabel } from './utils';

export const DeliveryAddressModal = () => {
  const { formatMessage } = useIntl();

  const dismissAndReplace = useDismissAndReplace();

  const [state, dispatch] = useReducer(addressReducer, {
    error: null,
    address: null,
    status: ActionStatus.IDLE,
  });

  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const {
    StoreChangeDialog,
    // FIXME: we need to remove these 3 lines after the refactor for the store locator
    orderingUnavailableOpen,
    onConfirmLocateRestaurants,
    onDismissStoreUnavailable,
  } = useStoreContext();

  const {
    user,
    editDeliveryAddress,
    saveDeliveryAddress,
    removeDeliveryAddress,
  } = useAuthContext();
  const [createAddressView, _setCreateAddressView] = useState(false);
  const [interceptedClose, setInterceptedClose] = useState(false);

  const [isNewAddress, setIsNewAddress] = useState(false);
  const [addressBeingEdited, setAddressBeingEdited] = useState<IDeliveryAddress | null>(null);
  const [selected, setSelected] = useState('');
  const [addressComponents, setAddressComponents] = useState<IPlaceAddress | null>(null);
  const [message, setMessage] = useState('');
  const [formHasError, setFormHasError] = useState(false);
  const [shouldSaveInfoChecked, setShouldSaveInfoChecked] = useState(false);
  const [alias, setAlias] = useState<string>('');
  const [showTermsView, setShowTermsView] = useState(false);
  const [showtermsLink, setShowtermsLink] = useState(false);
  const [
    queryDeliveryRestaurant,
    { data: deliveryRestaurantData, error: deliveryRestaurantError },
  ] = useDeliveryRestaurantLazyQuery({ fetchPolicy: 'no-cache' });
  const { setDeliverySurchargeFee } = useServiceModeContext();
  const getDetails = usePlaceIdDetails();

  const {
    setDeliveryAddress,
    setDeliveryInstructions,
    setOrderPhoneNumber,
    setQuoteId,
    selectStore,
    selectServiceMode,
    deliveryInstructions,
    formShouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
  } = useOrderContext();

  const deliveryRadius = useFlag(LaunchDarklyFlag.DELIVERY_RADIUS_IN_MILES);
  const showDeliveryAddresses = Boolean(user?.details?.deliveryAddresses?.length);

  const formStatus =
    state.status === ActionStatus.QUOTE_UNAVAILABLE && showDeliveryAddresses
      ? FormStatus.Unavailable
      : createAddressView || !showDeliveryAddresses
      ? addressBeingEdited
        ? FormStatus.Edit
        : FormStatus.Add
      : FormStatus.Select;

  const header = formatMessage({
    id: showTermsView
      ? 'deliveryTermsHeading'
      : formStatus === FormStatus.Unavailable
      ? 'deliveryNotAvailable'
      : formStatus === FormStatus.Edit
      ? 'editAddress'
      : formStatus === FormStatus.Add
      ? 'deliveryAddNewAddress'
      : 'deliverySelectYourAddress',
    defaultMessage: showTermsView
      ? 'Delivery Terms'
      : formStatus === FormStatus.Edit
      ? 'Edit Address'
      : undefined,
  });

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Delivery Not Completed',
  });

  const handleQuoteResult = useCallback(
    async ({ store, deliveryQuote }: IGetClosestAvailableDeliveryRestaurantResult) => {
      // if store available, route to store
      if (store && deliveryQuote === DeliveryStatus.QUOTE_SUCCESSFUL) {
        dispatch({
          type: ActionStatus.SUCCESS,
        });

        return selectStore(
          store,
          async () => {
            if (state.address) {
              const { address, deliveryInstructions, phoneNumber, coordinates } = state.address;

              setDeliveryAddress({
                ...address,
                alias,
                latitude: coordinates?.lat ?? undefined,
                longitude: coordinates?.lng ?? undefined,
              });

              const userDeliveryAddresses = user?.details.deliveryAddresses || [];

              if (
                (user?.cognitoId &&
                  formShouldSaveDeliveryAddress &&
                  userDeliveryAddresses.length === 0) ||
                (user?.cognitoId && !addressExists(userDeliveryAddresses, address))
              ) {
                const SaveDeliveryAddressInput: ISaveDeliveryAddressInput = {
                  addressLine1: address.addressLine1,
                  addressLine2: address.addressLine2,
                  alias: address.alias,
                  city: address.city,
                  state: address.state,
                  zip: address.zip,
                  country: address.country as IsoCountryCode,
                  phoneNumber,
                  defaultInstruction: address.defaultInstruction,
                  route: address.route,
                  streetNumber: address.streetNumber,
                  latitude: coordinates?.lat,
                  longitude: coordinates?.lng,
                };

                saveDeliveryAddress(SaveDeliveryAddressInput);
              }

              setShouldSaveDeliveryAddress(false);
              setFormShouldSaveDeliveryAddress(shouldSaveInfoChecked);
              setDeliveryInstructions(address.defaultInstruction || deliveryInstructions);
              setOrderPhoneNumber(phoneNumber);
            }

            const serviceMode = ServiceMode.DELIVERY;
            selectServiceMode(serviceMode);

            dismissAndReplace(routes.menu);
          },
          ServiceMode.DELIVERY
        );
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_UNAVAILABLE) {
        return dispatch({ type: ActionStatus.QUOTE_UNAVAILABLE });
      }

      if (deliveryQuote === DeliveryStatus.QUOTE_ERROR) {
        return dispatch({
          type: ActionStatus.ERROR,
          error: {
            message: formatMessage({ id: 'pleaseTryAgainLater' }),
            modalAppearanceEventMessage: 'Error: Delivery Quote Error',
          },
        });
      }
    },
    [
      alias,
      dismissAndReplace,
      formShouldSaveDeliveryAddress,
      formatMessage,
      saveDeliveryAddress,
      selectServiceMode,
      selectStore,
      setDeliveryAddress,
      setDeliveryInstructions,
      setFormShouldSaveDeliveryAddress,
      setOrderPhoneNumber,
      setShouldSaveDeliveryAddress,
      shouldSaveInfoChecked,
      state.address,
      user?.details.deliveryAddresses,
    ]
  );

  useEffect(() => {
    const restaurant = deliveryRestaurantData?.deliveryRestaurant?.restaurant;
    if (!restaurant) {
      return;
    }

    logRBIEvent({
      name: 'Delivery Store Assigned',
      type: EventTypes.Other,
      attributes: {
        restaurantId: restaurant._id || '',
        storeId: restaurant.storeId || '',
        restaurantAddress: restaurant.physicalAddress?.address1 || '',
        restaurantZip: restaurant.physicalAddress?.postalCode || '',
        restaurantCity: restaurant.physicalAddress?.city || '',
        restaurantState: restaurant.physicalAddress?.stateProvince || '',
        restaurantCountry: restaurant.physicalAddress?.country || '',
      },
    });
  }, [deliveryRestaurantData]);

  useEffect(() => {
    if (deliveryRestaurantData?.deliveryRestaurant) {
      const {
        quote,
        restaurant,
        storeStatus,
        nextEarliestOpen,
        deliverySurchargeFeeCents,
        quoteId,
      } = deliveryRestaurantData?.deliveryRestaurant;

      if (deliverySurchargeFeeCents) {
        setDeliverySurchargeFee(deliverySurchargeFeeCents);
      }

      setQuoteId(quoteId || '');

      handleQuoteResult({
        // @ts-expect-error TS(2322) FIXME: Type 'DeliveryStatus | null | undefined' is not as... Remove this comment to see the full error message
        deliveryQuote: quote,
        store: restaurant as IRestaurant,
        storeStatus: DeliveryStoreStatusToStoreStatusMap[storeStatus],
        nextEarliestOpen: nextEarliestOpen ? new Date(nextEarliestOpen) : undefined,
      });
    }

    /*
     * TODO: `handleQuoteResult` is a created by `useCallback`. It's updated
     * whenever `selectServiceMode` is updated, which is also created by
     * `useCallback`. `selectServiceMode` is updated whenever the `serviceMode`
     * state variable is changed. `selectServiceMode` is called in
     * `handleQuoteResult` causing an infinite render loop.
     *
     * Leaving `handleQuoteResult` out of the dependency array solves the issue
     * for now, but might needs a closer look as we organize state more effectively.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryRestaurantData]);

  const completeDelivery = async (data: SelectedDeliveryAddress, savedAddress = false) => {
    if (state.status === ActionStatus.PENDING) {
      return;
    }

    if (!data.address) {
      return dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Missing address information for delivery' },
      });
    }

    const {
      address,
      phoneNumber,
      coordinates: selectedCoordinates,
      placeId: selectedPlaceId,
    } = data;

    dispatch({ type: ActionStatus.PENDING, address: data });

    try {
      const placeId = selectedPlaceId;

      // TODO: RN - In very rare situations you could end up here without a placeId. Add an Address to PlaceId lookup back
      // if (!selectedCoordinates && !selectedPlaceId) {
      //   // only get the placeId if we need it to look up coordinates
      //   placeId = await getPlaceId(getAddressLabel(data));
      // }

      // TODO: RN - Is this extra place lookup really needed?
      const { coordinates } = selectedCoordinates
        ? { coordinates: selectedCoordinates }
        : await getDetailsPlaceData(placeId);

      if (!coordinates) {
        throw new Error('Unable to get coordinates from selected address');
      }

      setIsNewAddress(!savedAddress);

      queryDeliveryRestaurant({
        variables: {
          dropoff: {
            ...address,
            latitude: coordinates.lat,
            longitude: coordinates.lng,
            phoneNumber,
          },
          searchRadius: convertMilesToMeters(deliveryRadius),
          platform: platform(),
        },
      });
    } catch (error) {
      dispatch({
        type: ActionStatus.ERROR,
        error: { message: 'Error using delivery address', error: error as any },
      });
    }
  };

  useEffect(() => {
    if (!deliveryRestaurantData || deliveryRestaurantError) {
      return;
    }

    const storeId = deliveryRestaurantData?.deliveryRestaurant.restaurant?.storeId;

    let failureReason;
    if (
      deliveryRestaurantData?.deliveryRestaurant.storeStatus === DeliveryStoreStatus.NO_DELIVERY
    ) {
      failureReason = 'delivery unavailable';
    } else if (deliveryRestaurantError) {
      failureReason = 'Delivery restaurant API fail';
    } else if (!storeId) {
      failureReason = 'Restaurant not found';
    }

    logRBIEvent(
      {
        name: CustomEventNames.DELIVERY_ADDRESS_SUBMITTED,
        type: EventTypes.Other,
        attributes: {
          storeId: storeId || undefined,
          status: failureReason ? 'fail' : 'success',
          failureReason,
          searchType: isNewAddress ? 'newAddress' : 'savedAddress',
        },
      },
      { skipLoggingToBraze: true }
    );
  }, [deliveryRestaurantData, deliveryRestaurantError, isNewAddress]);

  const getDetailsPlaceData = useCallback(
    (pId: string | undefined): Promise<IPlaceData> => {
      return getDetails(pId).then(placeData => {
        if (placeData) {
          return Promise.resolve(placeData);
        }

        logNavigationClick(CustomEventNames.BUTTON_CLICK_DELIVER_HERE, {
          error: 'Address Not Recognized',
        });

        return Promise.reject(placeData);
      });
    },
    [getDetails]
  );

  const handleOnDeliveryHereButton = () => {
    selectRef.current?.onSelectAddress();
  };

  useEffect(() => {
    if (state.status === ActionStatus.ERROR && state.error) {
      openErrorDialog(state.error);
    }
  }, [openErrorDialog, state.error, state.status]);

  const showAddresses = showDeliveryAddresses && !createAddressView;

  // to avoid flatlists nested in a scrollview or else react-native complains
  const Container = showAddresses ? ScrollView : View;
  const formRef = useRef<DeliveryAddressFormHandle>(null);
  const selectRef = useRef<DeliveryAddressSelectHandle>(null);

  const isLoading = state.status === ActionStatus.PENDING;
  const DeliveryAddressesButtons = () => (
    <ButtonContainer>
      <DeliverHereButton
        testID="select-delivery-address"
        disabled={isLoading || selected === ''}
        onPress={handleOnDeliveryHereButton}
        bg={isLoading || selected === '' ? primitive.bk.$fieryRedOpacity30 : undefined}
        _text={{
          color: isLoading ? primitive.bk.$bbqBrown : primitive.bk.$white,
        }}
        rightIcon={
          isLoading ? (
            <Box width="$4" height="$4" justifyContent="center" alignItems="center">
              <Spinner size="sm" color={primitive.bk.$white} />
            </Box>
          ) : undefined
        }
      >
        {formatMessage({ id: 'deliverHere' })}
      </DeliverHereButton>
    </ButtonContainer>
  );

  const BackButton = ({ onPress }: { onPress: () => void }) => (
    <TouchableOpacity onPress={onPress}>
      <Icon variant="back" />
    </TouchableOpacity>
  );

  const onPressBackButton = () => {
    if (showTermsView) {
      setShowTermsView(false);
      return;
    }
    const hasChanges = formRef.current?.hasUnsavedChanges?.();

    if (hasChanges) {
      setInterceptedClose(true);
    } else {
      _setCreateAddressView(false);
      setAddressBeingEdited(null);
    }
  };
  const handleModalClose = () => {
    const hasChanges = formRef.current?.hasUnsavedChanges?.();

    if (showTermsView) {
      setShowTermsView(false);
      return;
    }

    if (hasChanges) {
      setInterceptedClose(true);
    } else {
      _setCreateAddressView(false);
      setAddressBeingEdited(null);
      router.dismiss();
    }
  };

  const ModalFooter = () => {
    if (state.status === ActionStatus.QUOTE_UNAVAILABLE || showTermsView) {
      return null;
    }

    if (!createAddressView && showDeliveryAddresses) {
      return <DeliveryAddressesButtons />;
    }

    return (
      <DeliveryAddressFormFooter
        isEditing={!!addressBeingEdited}
        submitAddress={() => formRef.current?.handleSubmit()}
        isDisabled={!addressComponents?.addressLine1 || formHasError}
        isLoading={state.status === ActionStatus.PENDING}
        showtermsLink={showtermsLink}
        setShowTermsView={setShowTermsView}
      />
    );
  };

  return (
    <CardModal
      headerText={header}
      customHeaderLeft={
        createAddressView || showTermsView ? <BackButton onPress={onPressBackButton} /> : null
      }
      onDismiss={handleModalClose}
      footerBackgroundColor={!showtermsLink ? primitive.bk.$white : primitive.bk.$mayoEggWhite}
      disableFooterBorder={showtermsLink}
      analyticsEvent={'Select your delivery address'}
      scrollView={true}
      noDesktopWebMaxWidth={true}
      footer={<ModalFooter />}
    >
      <Container style={{ flex: 1 }} keyboardShouldPersistTaps="handled" testID="delivery-modal">
        {state.status === ActionStatus.QUOTE_UNAVAILABLE ? (
          <DeliveryUnavailable
            address={state.address ? getAddressLabel(state.address) : ''}
            close={() => dispatch({ type: ActionStatus.IDLE })}
          />
        ) : (
          <DeliveryModalContainer testID="delivery-modal-container">
            {showTermsView ? (
              <DeliveryTermsContent />
            ) : showAddresses ? (
              <DeliveryAddresses
                ref={selectRef}
                addresses={user?.details?.deliveryAddresses || []}
                onCreate={() => {
                  _setCreateAddressView(true);
                  setAddressBeingEdited(null);
                }}
                selected={selected}
                setSelected={setSelected}
                setAddress={address => completeDelivery(address, false)}
                isLoading={state.status === ActionStatus.PENDING}
                removeDeliveryAddress={removeDeliveryAddress}
                onEditAddress={addr => {
                  setAddressBeingEdited(addr);
                  setShouldSaveInfoChecked(true);
                  _setCreateAddressView(true);
                }}
                setShowTermsView={setShowTermsView}
              />
            ) : (
              <DeliveryAddressForm
                ref={formRef}
                instructions={deliveryInstructions}
                getDetailsPlaceData={getDetailsPlaceData}
                setCreateAddressView={_setCreateAddressView}
                showDeliveryAddresses={showDeliveryAddresses}
                user={user}
                setAddress={completeDelivery}
                isLoading={state.status === ActionStatus.PENDING}
                addressToEdit={addressBeingEdited}
                editDeliveryAddress={editDeliveryAddress}
                addressComponents={addressComponents}
                setAddressComponents={setAddressComponents}
                message={message}
                setMessage={setMessage}
                clearAddressBeingEdited={() => setAddressBeingEdited(null)}
                deliveryAddresses={user?.details?.deliveryAddresses || []}
                setFormHasError={setFormHasError}
                shouldSaveInfoChecked={shouldSaveInfoChecked}
                setShouldSaveInfoChecked={setShouldSaveInfoChecked}
                alias={alias}
                setAlias={setAlias}
                interceptedClose={interceptedClose}
                setInterceptedClose={setInterceptedClose}
                dispatch={dispatch}
                setShowtermsLink={setShowtermsLink}
              />
            )}
          </DeliveryModalContainer>
        )}

        <ErrorDialog testID="address-modal-error-dialog" />
        <StoreChangeDialog heading={formatMessage({ id: 'changeStores' })} />
        {/* all this dialog is a hack to temporary solve the issue that we have a modal
      inside a modal is not showed on IOS, this is happening just on IOS and seems to
      be related on how ios manage the modals https://rbictg.atlassian.net/browse/BKPE-4595 */}
        {isNativeIOS() && orderingUnavailableOpen && (
          <Dialog
            showDialog
            heading={
              isObject(orderingUnavailableOpen) && orderingUnavailableOpen.headingOverride
                ? orderingUnavailableOpen.headingOverride
                : formatMessage({ id: 'storeClosed' })
            }
            body={
              isObject(orderingUnavailableOpen) && orderingUnavailableOpen?.bodyOverride
                ? orderingUnavailableOpen?.bodyOverride
                : formatMessage({
                    id: enableOrdering ? 'cannotPlaceOrder' : 'closeMessageToFindNearbyRestaurants',
                  })
            }
            confirmButtonLabel={formatMessage({ id: 'findRestaurants' })}
            confirmDialog={onConfirmLocateRestaurants}
            onDismiss={onDismissStoreUnavailable}
            modalAppearanceEventMessage="Ordering is unavailable"
            isErrorModal
          />
        )}
      </Container>
    </CardModal>
  );
};
